import type {
  SourceUsageChartPoint,
  SourceUsageChartSeriesItem,
} from "../../../components/OverviewSourceUsage/types";
import { formatSourceUsageDate } from "../../../components/OverviewSourceUsage/utils/formatSourceUsageDate";
import type { SourceDetailLinkRow } from "../types";

const SOURCE_DETAIL_CHART_COLOR = "var(--aicarma-color-green)";
const TOP_URLS_COUNT = 5;
const URL_DISPLAY_MAX_LENGTH = 50;

export interface NormalizedSourceDetailUrlsChart {
  data: SourceUsageChartPoint[];
  series: SourceUsageChartSeriesItem[];
}

function truncateUrl(url: string): string {
  if (url.length <= URL_DISPLAY_MAX_LENGTH) return url;
  return url.slice(0, URL_DISPLAY_MAX_LENGTH - 3) + "...";
}

/**
 * Builds chart data from source detail links: top 5 URLs by usage (count),
 * one point per date with usage % per URL. All series use the same green color.
 */
export function normalizeSourceDetailUrlsChart(
  rows: SourceDetailLinkRow[] | null
): NormalizedSourceDetailUrlsChart {
  if (!rows || rows.length === 0) {
    return { data: [], series: [] };
  }

  const countByUrl = new Map<string, number>();
  const countByDateAndUrl = new Map<string, Map<string, number>>();
  const datesSet = new Set<string>();

  for (const r of rows) {
    const date = r.response_date;
    if (!date) continue;
    datesSet.add(date);
    countByUrl.set(r.url, (countByUrl.get(r.url) ?? 0) + 1);
    let byUrl = countByDateAndUrl.get(date);
    if (!byUrl) {
      byUrl = new Map<string, number>();
      countByDateAndUrl.set(date, byUrl);
    }
    byUrl.set(r.url, (byUrl.get(r.url) ?? 0) + 1);
  }

  const sortedUrls = [...countByUrl.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_URLS_COUNT)
    .map(([url]) => url);

  const dates = [...datesSet].sort();
  const data: SourceUsageChartPoint[] = dates.map((responseDate) => {
    const byUrl = countByDateAndUrl.get(responseDate);
    const dayTotal = byUrl
      ? [...byUrl.values()].reduce((s, n) => s + n, 0)
      : 0;
    const point: SourceUsageChartPoint = {
      date: formatSourceUsageDate(responseDate),
    };
    sortedUrls.forEach((url, i) => {
      const key = `url_${i}`;
      const count = byUrl?.get(url) ?? 0;
      const value =
        dayTotal > 0 ? (count / dayTotal) * 100 : 0;
      point[key] = Math.round(value * 10) / 10;
    });
    return point;
  });

  const series: SourceUsageChartSeriesItem[] = sortedUrls.map((url, i) => ({
    dataKey: `url_${i}`,
    name: truncateUrl(url),
    color: SOURCE_DETAIL_CHART_COLOR,
  }));

  return { data, series };
}
